import classNames from "classnames";
import { useState, useEffect, useRef, cloneElement } from "react";
import { useRouter } from "next/router";

import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import PaymentForm from "../components/PaymentForm";

import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import BankAccountForm from "../components/BankAccountForm";
import Head from "next/head";

const STRIPE_PUBLIC_KEY = process.env.STRIPE_PUBLIC_KEY;
const ENDPOINT = process.env.ENDPOINT;

const promise = loadStripe(STRIPE_PUBLIC_KEY);

const SectionHeader = ({ index, label, sublabel }) => (
  <div className="flex items-center sm:col-span-2 mt-6 mb-2">
    <div className="sm:-ml-16 rounded-full h-12 w-12 p-4 flex items-center justify-center text-lg font-black bg-red-500 text-white">
      {index}
    </div>
    <div className="ml-4">
      <div className="text-lg font-semibold text-gray-700">{label}</div>
      <div className="text-gray-500 leading-2">{sublabel}</div>
    </div>
  </div>
);

const Option = ({ label, icon, selected, onClick }) => (
  <div
    className={classNames(
      "cursor-pointer form-input rounded-md hover:shadow-outline-blue px-4 py-4 flex items-center justify-center transition duration-150 ease-in-out",
      selected && "text-gray-100 bg-red-500"
    )}
    tabIndex="0"
    onClick={() => onClick()}
    onKeyPress={(e) => {
      if (e.key === "Enter") {
        onClick();
      }
    }}
  >
    <div>{icon}</div>
    <div className="font-medium text-md text-center">{label}</div>
  </div>
);

const Select = ({ value, children, onChange, listOnMobile }) => {
  return (
    <div
      // grid-cols-1 grid-cols-2 grid-cols-3 grid-cols-4 grid-cols-5
      // sm:grid-cols-1 sm:grid-cols-2 sm:grid-cols-3 sm:grid-cols-4 sm:grid-cols-5
      className={`mt-3 grid ${listOnMobile ? "grid-cols-1 sm:" : ""}grid-cols-${
        children.length === 5 ? 3 : Math.min(children.length, 4)
      } gap-4`}
    >
      {children.map((c) =>
        cloneElement(c, {
          key: c.props.value,
          selected: value === c.props.value,
          onClick: () => onChange(c.props.value),
        })
      )}
    </div>
  );
};

const RadioItem = ({ id, label, checked, onSelect }) => (
  <div class="mt-2 flex items-center">
    <input
      id={id}
      checked={checked}
      onChange={() => onSelect()}
      type="radio"
      class="form-radio h-4 w-4 text-red-600 transition duration-150 ease-in-out"
    />
    <label htmlFor={id} class="ml-3">
      <span class="block text-sm leading-5 font-medium text-gray-700">
        {label}
      </span>
    </label>
  </div>
);

export default function Contact() {
  const router = useRouter();

  const [values, setValues] = useState({
    organization_id: "",
    organization: "",
    type: null,
    goal: null,
    first_name: "",
    last_name: "",
    email: "",
    message: "",

    // goal = activation
    activation_type: "virtual_annual",
    payment_source: "new",
    terms_agree: false,
  });

  const paymentRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);

  const readyToSubmit = values.goal !== "activate" || values.terms_agree;

  const updateValues = (data) => {
    setValues({ ...values, ...data });
  };

  useEffect(() => {
    const newValues = {};
    for (const k in values) {
      if (router.query[k]) {
        newValues[k] = router.query[k];
      }
    }
    if (router.query.existing_sources) {
      const sources = JSON.parse(router.query.existing_sources);
      if (sources.length) {
        newValues.payment_source = sources[0].id;
      }
    }
    updateValues(newValues);
  }, [router.query]);

  useEffect(() => {
    if (success) {
      if (window.gtag) {
        window.gtag("event", "conversion", {
          send_to: `${process.env.GOOGLE_ANALYTICS_CONVERSION_TRACKING_ID}/SKRICP3atpAZENq62MwD`,
        });
      }
    }
  }, [success]);

  const onSubmit = async (event) => {
    event.preventDefault();

    if (!readyToSubmit) {
      return;
    }

    setSubmitting(true);
    setError(null);

    grecaptcha.ready(async () => {
      let captcha_token;
      try {
        captcha_token = await grecaptcha.execute(
          process.env.RECAPTCHA_SITE_TOKEN,
          {
            action: "submit",
          }
        );
      } catch (e) {
        setError(e.message);
        setSubmitting(false);
        return;
      }

      let data = { ...values, captcha_token };

      if (
        values.goal === "activate" &&
        (values.activation_type === "virtual_annual" ||
          values.activation_type === "virtual_single") &&
        values.payment_source === "new"
      ) {
        try {
          const token = await paymentRef.current.createToken();
          data.payment_token = token.id;
        } catch (e) {
          setError(e.message);
          setSubmitting(false);
          return;
        }
      }

      try {
        const res = await fetch(`${ENDPOINT}/activations/submit`, {
          method: "POST",
          headers: {
            "content-type": "application/json",
          },
          body: JSON.stringify(data),
        }).then((r) => r.json());
        if (res.error) {
          throw new Error(res.error.message || "An unknown error occurred.");
        }
        setSuccess(true);
        setSubmitting(false);
        window.scrollTo(0, 0);
      } catch (e) {
        setError(e.message);
        setSubmitting(false);
      }
    });
  };

  return (
    <Layout title="Get Started" short>
      <Head>
        <script
          src={`https://www.google.com/recaptcha/api.js?render=${process.env.RECAPTCHA_SITE_TOKEN}`}
        ></script>
      </Head>
      <PageHeader
        label="Get Started with Eventive"
        headline={
          <span>
            Let's get{" "}
            <span className="hidden sm:inline">
              this <span className="mx-1">🎉</span>
            </span>{" "}
            started!
          </span>
        }
        subhead={
          !values.organization_id ? (
            <div className="max-w-lg mx-auto text-base">
              Have a quick question?{" "}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  Intercom("showNewMessage", "");
                }}
              >
                <span className="ml-0 mt-2 hover:-mr-1 inline-block md:ml-1 text-gray-600 font-medium bg-gray-200 hover:text-gray-700 hover:bg-gray-300 transition-all duration-150 ease-in-out rounded-full px-3 cursor-pointer group">
                  Start a chat
                  <svg
                    className="h-4 w-4 ml-2 group-hover:ml-3 inline-block transition-all duration-150 ease-in-out"
                    style={{ verticalAlign: -2 }}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                      fillRule="evenodd"
                    ></path>
                  </svg>
                </span>
              </a>
            </div>
          ) : null
        }
      />
      <div className="bg-white pb-16 px-4 sm:px-6 lg:px-8 lg:pb-24">
        <div className="relative max-w-xl mx-auto">
          <svg
            className="absolute left-full transform translate-x-1/2"
            style={{ top: -200 }}
            width="404"
            height="404"
            fill="none"
            viewBox="0 0 404 404"
          >
            <defs>
              <pattern
                id="85737c0e-0916-41d7-917f-596dc7edfa27"
                x="0"
                y="0"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x="0"
                  y="0"
                  width="4"
                  height="4"
                  className="text-gray-200"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width="404"
              height="404"
              fill="url(#85737c0e-0916-41d7-917f-596dc7edfa27)"
            />
          </svg>
          <svg
            className="absolute right-full bottom-0 transform -translate-x-1/2"
            width="404"
            height="404"
            fill="none"
            viewBox="0 0 404 404"
          >
            <defs>
              <pattern
                id="85737c0e-0916-41d7-917f-596dc7edfa27"
                x="0"
                y="0"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x="0"
                  y="0"
                  width="4"
                  height="4"
                  className="text-gray-200"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width="404"
              height="404"
              fill="url(#85737c0e-0916-41d7-917f-596dc7edfa27)"
            />
          </svg>
          <div className="mt-6 sm:mt-12">
            {success ? (
              values.goal === "activate" ? (
                <div>
                  <div className="mt-12 sm:mt-24 text-center mx-auto max-w-md">
                    <div className="text-xl font-semibold text-gray-700 mb-2 leading-tight">
                      Your activation was processed successfully.
                    </div>
                    <div className="text-gray-500">
                      {values.activation_type === "virtual_annual"
                        ? `Your payment of US$975 was processed successfully. `
                        : values.activation_type === "virtual_single"
                        ? `Your payment of US$299 was processed successfully. `
                        : ""}
                      {values.organization_id
                        ? `Your account is all ready to use. If we haven't already, our team will reach out to schedule an onboarding session soon. We're thrilled to welcome you to the Eventive family!`
                        : `Your account will be fully activated shortly, and a member of our Organizer Success team will reach out soon with access information. Welcome to the Eventive family!`}
                    </div>
                    {router.query.next ? (
                      <a
                        href={router.query.next}
                        className={classNames(
                          "mt-4 inline-flex items-center justify-center px-4 py-2 border border-transparent text-base leading-6 font-medium rounded-md text-white bg-red-600 hover:bg-red-500 focus:outline-none focus:border-red-700 focus:shadow-outline-red active:bg-red-700 transition ease-in-out duration-150"
                        )}
                      >
                        Return to Dashboard
                      </a>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="mt-12 sm:mt-24 text-center">
                  <div className="text-xl font-semibold text-gray-700 mb-2 leading-tight">
                    Thanks, we've received your information.
                  </div>
                  <div className="inline sm:block text-gray-500">
                    Our team will be in touch via email within 1 business day.
                  </div>{" "}
                  <div className="inline sm:block text-gray-500 sm:mt-1">
                    Questions in the interim? Please{" "}
                    <a
                      href="#"
                      className="underline hover:opacity-75"
                      onClick={(e) => {
                        e.preventDefault();
                        Intercom("showNewMessage", "");
                      }}
                    >
                      send us a chat
                    </a>
                    .
                  </div>
                </div>
              )
            ) : (
              <form
                onSubmit={onSubmit}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
                className="mx-4 grid grid-cols-1 row-gap-6 sm:grid-cols-2 sm:col-gap-8"
              >
                <SectionHeader
                  index="1"
                  label="Organization details"
                  sublabel="Tell us a bit about your organization."
                />

                <div className="sm:col-span-2">
                  <label
                    htmlFor="organization"
                    className="block text-sm font-medium leading-5 text-gray-700"
                  >
                    Organization name
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <input
                      id="organization"
                      required
                      value={values.organization}
                      onChange={(e) =>
                        updateValues({ organization: e.target.value })
                      }
                      className="form-input py-3 px-4 block w-full transition ease-in-out duration-150"
                    />
                  </div>
                </div>

                {!values.organization_id ? (
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium leading-5 text-gray-700">
                      Organization type
                    </label>
                    <Select
                      value={values.type}
                      onChange={(v) => updateValues({ type: v })}
                    >
                      <Option
                        value="film_festival"
                        label="Film festival"
                        icon={null}
                      />
                      <Option
                        value="distributor"
                        label="Distributor"
                        selected
                        icon={null}
                      />
                      <Option value="cinema" label="Cinema" icon={null} />
                      <Option value="filmmaker" label="Filmmaker" icon={null} />
                      <Option value="other" label="Other" icon={null} />
                    </Select>
                  </div>
                ) : null}

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium leading-5 text-gray-700">
                    Current goal
                  </label>
                  <Select
                    value={values.goal}
                    onChange={(v) => updateValues({ goal: v })}
                    listOnMobile
                  >
                    {values.organization_id && values.goal === "activate"
                      ? [
                          <Option
                            value="activate"
                            label="Activate existing account"
                            icon={null}
                          />,
                        ]
                      : [
                          <Option
                            value="learn_more"
                            label={
                              <span>
                                Talk to sales /{" "}
                                <span className="inline sm:block">
                                  see a demo
                                </span>
                              </span>
                            }
                            icon={null}
                          />,
                          <Option
                            value="trial"
                            label="Open a trial account"
                            icon={null}
                          />,
                          <Option
                            value="activate"
                            label="Activate / sign up right now"
                            icon={null}
                          />,
                        ]}
                  </Select>
                </div>

                <SectionHeader
                  index="2"
                  label="Contact details"
                  sublabel="How should we get in touch?"
                />

                <div>
                  <label
                    htmlFor="first_name"
                    className="block text-sm font-medium leading-5 text-gray-700"
                  >
                    First name
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <input
                      id="first_name"
                      required
                      value={values.first_name}
                      onChange={(e) =>
                        updateValues({ first_name: e.target.value })
                      }
                      className="form-input py-3 px-4 block w-full transition ease-in-out duration-150"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="last_name"
                    className="block text-sm font-medium leading-5 text-gray-700"
                  >
                    Last name
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <input
                      id="last_name"
                      required
                      value={values.last_name}
                      onChange={(e) =>
                        updateValues({ last_name: e.target.value })
                      }
                      className="form-input py-3 px-4 block w-full transition ease-in-out duration-150"
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium leading-5 text-gray-700"
                  >
                    Work email address
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <input
                      id="email"
                      required
                      type="email"
                      value={values.email}
                      onChange={(e) => updateValues({ email: e.target.value })}
                      className="form-input py-3 px-4 block w-full transition ease-in-out duration-150"
                    />
                  </div>
                </div>

                {values.goal === "activate" ? (
                  <>
                    <SectionHeader
                      index="3"
                      label="Activation details"
                      sublabel="Let's activate your account."
                    />

                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium leading-5 text-gray-700">
                        Activation type
                      </label>
                      <div className="mt-1">
                        <RadioItem
                          id="virtual_annual"
                          label="Eventive Virtual Unlimited annual activation ($975/year)"
                          checked={values.activation_type === "virtual_annual"}
                          onSelect={() =>
                            updateValues({ activation_type: "virtual_annual" })
                          }
                        />
                        {values.type === "filmmaker" ||
                        values.type === "other" ? (
                          <RadioItem
                            id="virtual_single"
                            label="Eventive Virtual Single Film activation ($299/film)"
                            checked={
                              values.activation_type === "virtual_single"
                            }
                            onSelect={() =>
                              updateValues({
                                activation_type: "virtual_single",
                              })
                            }
                          />
                        ) : null}
                        <RadioItem
                          id="default"
                          label="Eventive for physical events only ($0)"
                          checked={values.activation_type === "default"}
                          onSelect={() =>
                            updateValues({ activation_type: "default" })
                          }
                        />
                        <RadioItem
                          id="other"
                          label="Something else"
                          checked={values.activation_type === "other"}
                          onSelect={() =>
                            updateValues({ activation_type: "other" })
                          }
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <label
                        htmlFor="type"
                        className="block text-sm font-medium leading-5 text-gray-700"
                      >
                        Activation payment method
                      </label>
                      {!["virtual_annual", "virtual_single"].includes(
                        values.activation_type
                      ) ? (
                        <div className="mt-2 text-gray-600 text-sm">
                          No payment required.
                        </div>
                      ) : (
                        <div className="mt-2">
                          {(
                            (router.query.existing_sources &&
                              JSON.parse(router.query.existing_sources)) ||
                            []
                          ).map((source) => (
                            <RadioItem
                              key={source.id}
                              id={source.id}
                              label={source.label}
                              checked={values.payment_source === source.id}
                              onSelect={() =>
                                updateValues({
                                  payment_source: source.id,
                                })
                              }
                            />
                          ))}
                          <RadioItem
                            id="new"
                            label="New credit card"
                            checked={values.payment_source === "new"}
                            onSelect={() =>
                              updateValues({ payment_source: "new" })
                            }
                          />
                          <RadioItem
                            id="new_bank_account"
                            label="New U.S. bank account"
                            checked={
                              values.payment_source === "new_bank_account"
                            }
                            onSelect={() =>
                              updateValues({
                                payment_source: "new_bank_account",
                              })
                            }
                          />
                          {values.payment_source === "new" ? (
                            <div className="mt-3">
                              <Elements stripe={promise}>
                                <PaymentForm
                                  onChange={(e) => {
                                    paymentRef.current = e.ref;
                                  }}
                                />
                              </Elements>
                            </div>
                          ) : values.payment_source === "new_bank_account" ? (
                            <div className="mt-4 text-gray-600 text-sm">
                              {values.bank_account_label ? (
                                `${values.bank_account_label} successfully linked & ready to charge.`
                              ) : (
                                <div>
                                  <BankAccountForm
                                    onSubmit={(public_token, metadata) => {
                                      updateValues({
                                        bank_account_public_token: public_token,
                                        bank_account_id: metadata.account_id,
                                        bank_account_label: metadata.account
                                          ? `${metadata.account.name} ending in ${metadata.account.mask}`
                                          : `Bank account`,
                                      });
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>

                    <div className="sm:col-span-2">
                      <label
                        htmlFor="type"
                        className="block text-sm font-medium leading-5 text-gray-700"
                      >
                        Activation terms
                      </label>
                      <div class="mt-3 flex items-start">
                        <div class="absolute flex items-center h-5">
                          <input
                            id="terms"
                            type="checkbox"
                            checked={values.terms_agree}
                            onChange={(e) =>
                              updateValues({ terms_agree: !values.terms_agree })
                            }
                            class="form-checkbox h-4 w-4 text-red-600 transition duration-150 ease-in-out"
                          />
                        </div>
                        <div class="pl-7 text-sm leading-5">
                          <label
                            htmlFor="terms"
                            class="font-medium text-gray-700"
                          >
                            I agree to the Eventive{" "}
                            <a
                              href="/terms"
                              target="_blank"
                              className="underline hover:opacity-75"
                            >
                              Terms
                            </a>{" "}
                            and{" "}
                            <a
                              href="/privacy"
                              target="_blank"
                              className="underline hover:opacity-75"
                            >
                              Privacy Policy
                            </a>
                            .
                          </label>
                          {values.activation_type === "virtual_annual" ||
                          values.activation_type === "virtual_single" ? (
                            <p class="text-gray-500 mt-1">
                              I understand the my selected payment method will
                              be charged immediately
                              {values.activation_type === "virtual_annual" ? (
                                <>
                                  , and that my annual access will expire unless
                                  renewed on{" "}
                                  {new Intl.DateTimeFormat("en-US").format(
                                    new Date(
                                      new Date().setFullYear(
                                        new Date().getFullYear() + 1
                                      )
                                    )
                                  )}
                                </>
                              ) : null}
                              . I also understand that I will receive a monthly
                              bill for any charges incurred during my use of the
                              Eventive platform
                              {values.activation_type === "virtual_single" ? (
                                <>
                                  , including the Eventive Ticketing 5% +
                                  US$0.99 transaction fee on any paid orders, as
                                  well as the US$0.99 DRM streaming bandwidth
                                  fee per unlock beyond my free allotment. I
                                  understand that if I upload any additional
                                  films, I will be billed $299 per film. All
                                  Sales Are Final.
                                </>
                              ) : (
                                <>.</>
                              )}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <SectionHeader
                      index="3"
                      label="More information"
                      sublabel="Let us know how we can best help your organization."
                    />

                    <div className="sm:col-span-2">
                      <label
                        htmlFor="message"
                        className="block text-sm font-medium leading-5 text-gray-700"
                      >
                        Optional message
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <textarea
                          id="message"
                          value={values.message}
                          onChange={(e) =>
                            updateValues({ message: e.target.value })
                          }
                          rows="4"
                          className="form-textarea py-3 px-4 block w-full transition ease-in-out duration-150"
                        ></textarea>
                      </div>
                    </div>
                  </>
                )}
                <div className="mt-6 sm:col-span-2">
                  <span className="w-full inline-flex rounded-md shadow-sm">
                    <button
                      type="submit"
                      disabled={submitting || !readyToSubmit}
                      className={classNames(
                        "w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base leading-6 font-medium rounded-md text-white bg-red-600 hover:bg-red-500 focus:outline-none focus:border-red-700 focus:shadow-outline-red active:bg-red-700 transition ease-in-out duration-150",
                        (submitting || !readyToSubmit) &&
                          "opacity-50 select-none pointer-events-none"
                      )}
                    >
                      {submitting
                        ? "Loading..."
                        : values.goal === "activate"
                        ? `Activate my account (${
                            values.activation_type === "virtual_annual"
                              ? "$975"
                              : values.activation_type === "virtual_single"
                              ? "$299"
                              : "$0"
                          })`
                        : values.goal === "trial"
                        ? "Start my trial"
                        : "Let's talk"}
                    </button>
                  </span>
                </div>
                {error ? (
                  <div className="sm:col-span-2 text-center">
                    {error} Having trouble?{" "}
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        Intercom("showNewMessage", "");
                      }}
                      className="underline hover:opacity-75"
                    >
                      Start a live chat.
                    </a>
                  </div>
                ) : null}
              </form>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
